/**
 * propuestas.ts — Contrato view-model `Propuesta` (P2RED-S1-3a).
 *
 * Implementa el contrato normativo §3 del AUDIT
 * `.ptel/p0_p2dprop_20260708/AUDIT_P2_MODELO_B_PROPUESTA.md` y la proyección
 * §4 de las 4 categorías fuente (Correcciones · Sugerencias IA · Abreviaturas
 * · Diccionario) hacia una forma común que la ficha consume.
 *
 * Es un VIEW-MODEL: no sustituye a los tipos fuente ni al almacenamiento;
 * los proyecta. Invariantes duros (§6 del AUDIT):
 *  1. `original` es INMUTABLE — `confirmado` se guarda JUNTO a él, nunca encima.
 *  2. Transparencia: toda propuesta es `auto` reversible visible o `pending`
 *     mostrada inline; no existe tercer estado silencioso.
 *  3. `origen` persiste siempre (trazabilidad) pero NO aparece en la UI
 *     principal — solo en traza/detalle (decisión Luis 08-jul).
 *  4. `id` determinista por hash de contenido (no índice posicional):
 *     sobrevive a re-parseos del mismo documento.
 *
 * Cierra `OBS-P2-CONTRATO-PROPUESTA-ADAPTADOR` (sev B).
 */

// S4PODA (11-sep-2026): tipos del árbol de pestañas podado, ahora en `./types`.
import type { CorrectionData, SuggestionData, AbreviaturaData, DiccionarioEntry } from './types';

// ============================================================================
// TIPOS DEL CONTRATO (§3)
// ============================================================================

/** Discriminador de valor (crítica 4 del AUDIT): condiciona el render. */
export type ValueKind = 'texto' | 'coordenada';

/** Origen de la propuesta — SOLO traza/detalle, nunca UI principal (§6.3). */
export type PropuestaOrigen =
  | 'utf8' | 'chars' | 'format'   // deterministas (Correcciones)
  | 'coords'                      // corrección de coordenada (juicio)
  | 'ia'                          // sugerencia IA
  | 'abreviatura' | 'diccionario'; // reglas de texto

export type PropuestaStatus = 'auto' | 'pending' | 'approved' | 'rejected';

/** Par de coordenadas UTM30 (EPSG:25830). */
export interface CoordPair { x: number; y: number; epsg: 25830 }

/** Excepción por elemento sobre una regla (crítica 1, caso «Cañada Sur»). */
export interface ExcepcionElemento {
  status: 'approved' | 'rejected' | 'edited';
  /** Solo si `status === 'edited'` (p. ej. «Cañada Sur»). */
  valor?: string;
}

export interface Propuesta {
  /** Estable y determinista: hash(origen+field+original[+infraId]). */
  id: string;
  /** Ámbito por elemento (utf8/chars/format/coords/ia). Excluyente con `infraIds`. */
  infraId?: string;
  /** Ámbito de regla multi-elemento (abreviatura/diccionario). */
  infraIds?: string[];
  /** Campo de `ProcessedInfrastructure` afectado ('nombre'|'direccion'|'coordenadas'|…). */
  field: string;
  valueKind: ValueKind;
  /** INMUTABLE — valor del documento subido (§6.1). */
  original: string | CoordPair;
  /** Lo que el detector propone. */
  propuesto: string | CoordPair;
  /** Lo que el técnico fija; se guarda JUNTO al original, nunca encima. */
  confirmado?: string | CoordPair;
  /** 0-100, semántica per-origen (§5.3 del AUDIT). */
  confidence: number;
  status: PropuestaStatus;
  /** Justificación del detector (IA, coords) — visible al consultar. */
  reason?: string;
  origen: PropuestaOrigen;
  /** Crítica 1 — solo reglas (clave = infraId). */
  overrides?: Record<string, ExcepcionElemento>;
}

/** Entrada de la proyección: las 4 categorías tal y como salen de `adaptStep2Props`. */
export interface ProyeccionInput {
  corrections?: CorrectionData[];
  suggestions?: SuggestionData[];
  abreviaturas?: AbreviaturaData[];
  diccionarioEntries?: DiccionarioEntry[];
}

// ============================================================================
// ID DETERMINISTA (§6.4)
// ============================================================================

/** Serializa un valor del contrato para el hash. */
function valorAClave(v: string | CoordPair): string {
  return typeof v === 'string' ? v : `${v.x},${v.y}`;
}

/**
 * Id determinista FNV-1a 32 bits sobre `origen|field|original[|infraId]`.
 * No criptográfico: solo estabilidad entre re-parseos del mismo documento.
 */
export function idPropuesta(
  origen: PropuestaOrigen,
  field: string,
  original: string | CoordPair,
  infraId?: string,
): string {
  const s = `${origen}|${field}|${valorAClave(original)}|${infraId ?? ''}`;
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return `p-${(h >>> 0).toString(16).padStart(8, '0')}`;
}

// ============================================================================
// UTILIDADES DE VALOR
// ============================================================================

/**
 * Intenta interpretar el formato de presentación «X 435287 · Y 4123456» que
 * emite `adaptStep2Props` para correcciones de coordenada. Si no casa,
 * devuelve null y la propuesta se proyecta como `texto` (render degradado
 * honesto, sin inventar números).
 */
export function parseCoordDisplay(s: string): CoordPair | null {
  const m = /X\s+(-?[\d.]+)\s+·\s+Y\s+(-?[\d.]+)/.exec(s);
  if (m == null) return null;
  const x = parseFloat(m[1]);
  const y = parseFloat(m[2]);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  return { x, y, epsg: 25830 };
}

/** Distancia euclídea en metros entre dos pares UTM30 (Δ de la ficha, §5.4). */
export function deltaMetros(a: CoordPair, b: CoordPair): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

/** ¿La propuesta es una regla multi-elemento (abreviatura/diccionario)? */
export function esRegla(p: Propuesta): boolean {
  return p.infraIds !== undefined;
}

/**
 * Semántica de resolución regla + overrides (§3):
 * `overrides[k]?.valor ?? (overrides[k]?.status === 'rejected' ? original : propuesto)`.
 * El override nunca muta la regla ni afecta a otros elementos.
 */
export function valorEfectivo(p: Propuesta, infraId: string): string | CoordPair {
  const ov = p.overrides?.[infraId];
  if (ov?.status === 'edited' && ov.valor !== undefined) return ov.valor;
  if (ov?.status === 'rejected') return p.original;
  return p.confirmado ?? p.propuesto;
}

/** Propuestas que afectan a un elemento (ámbito directo o vía regla). */
export function propuestasDeElemento(propuestas: readonly Propuesta[], infraId: string): Propuesta[] {
  return propuestas.filter(p => p.infraId === infraId || (p.infraIds?.includes(infraId) ?? false));
}

// ============================================================================
// PROYECCIÓN §4 — las 4 categorías → contrato
// ============================================================================

/** Justificación derivable del tipo determinista (§4: reason ∅ → derivable). */
const REASON_POR_TIPO: Record<string, string> = {
  utf8: 'Corrección de codificación UTF-8',
  chars: 'Corrección de caracteres corruptos',
  format: 'Normalización de formato',
  coords: 'Corrección de coordenada del pipeline',
};

function deCorrection(c: CorrectionData): Propuesta {
  const esCoord = c.type === 'coords';
  const origPar = esCoord ? parseCoordDisplay(c.original) : null;
  const propPar = esCoord ? parseCoordDisplay(c.corrected) : null;
  // Solo es `coordenada` si AMBOS extremos parsean; si no, texto (degradación honesta).
  const kind: ValueKind = origPar != null && propPar != null ? 'coordenada' : 'texto';
  return {
    id: idPropuesta(c.type, c.field, c.original, c.infraId),
    ...(c.infraId !== undefined && { infraId: c.infraId }),
    field: c.field,
    valueKind: kind,
    original: kind === 'coordenada' ? (origPar as CoordPair) : c.original,
    propuesto: kind === 'coordenada' ? (propPar as CoordPair) : c.corrected,
    confidence: c.confidence,
    status: c.status,
    reason: REASON_POR_TIPO[c.type],
    origen: c.type,
  };
}

const STATUS_SUGERENCIA: Record<SuggestionData['status'], PropuestaStatus> = {
  new: 'pending',
  applied: 'approved',
  dismissed: 'rejected',
};

function deSuggestion(s: SuggestionData): Propuesta {
  // §4: field ∅ → campo derivado del type.
  const field = s.field ?? (s.type === 'typology' ? 'tipo' : 'coordenadas');
  const original = s.currentValue ?? '';
  const origPar = parseCoordDisplay(original);
  const propPar = parseCoordDisplay(s.suggestedValue);
  const kind: ValueKind = origPar != null && propPar != null ? 'coordenada' : 'texto';
  return {
    id: idPropuesta('ia', field, original, s.infraId),
    ...(s.infraId !== undefined && { infraId: s.infraId }),
    field,
    valueKind: kind,
    original: kind === 'coordenada' ? (origPar as CoordPair) : original,
    propuesto: kind === 'coordenada' ? (propPar as CoordPair) : s.suggestedValue,
    confidence: s.confidence,
    status: STATUS_SUGERENCIA[s.status],
    ...(s.reason !== undefined && { reason: s.reason }),
    origen: 'ia',
  };
}

function deAbreviatura(a: AbreviaturaData): Propuesta {
  return {
    // NOTA (gap fuente): la detección escanea nombre y dirección pero no
    // registra el campo portador del token → `field` genérico 'texto'.
    // Candidato a subsanar upstream (`HX-CAPTURA-MAXIMAL`) — ver STATUS S1-3a.
    id: idPropuesta('abreviatura', 'texto', a.original),
    infraIds: a.infraIds ?? [],
    field: 'texto',
    valueKind: 'texto',
    original: a.original,
    propuesto: a.expansion,
    confidence: a.confidence,
    status: a.status,
    reason: `Detectada en: ${a.source} (${a.occurrences} apariciones)`,
    origen: 'abreviatura',
  };
}

const STATUS_DICCIONARIO: Record<DiccionarioEntry['status'], PropuestaStatus> = {
  approved: 'approved',
  pending: 'pending',
  suggested: 'pending',
};

function deDiccionario(d: DiccionarioEntry): Propuesta {
  return {
    id: idPropuesta('diccionario', d.category, d.original),
    infraIds: d.infraIds ?? [],
    field: d.category,
    valueKind: 'texto',
    original: d.original,
    propuesto: d.normalized,
    confidence: d.confidence,
    status: STATUS_DICCIONARIO[d.status],
    reason: `Detectado en: ${d.source} (${d.occurrences} apariciones)`,
    origen: 'diccionario',
  };
}

/**
 * Proyección completa §4: adapta las 4 categorías de `adaptStep2Props` al
 * contrato `Propuesta`. Pura y determinista (mismo input → mismos ids).
 * `occurrences` no se pierde: viaja en `reason` y como `infraIds.length`
 * (`HX-CAPTURA-MAXIMAL`: lo no proyectado va al detalle, no se descarta).
 */
export function proyectarPropuestas(input: ProyeccionInput): Propuesta[] {
  const { corrections = [], suggestions = [], abreviaturas = [], diccionarioEntries = [] } = input;
  return [
    ...corrections.map(deCorrection),
    ...suggestions.map(deSuggestion),
    ...abreviaturas.map(deAbreviatura),
    ...diccionarioEntries.map(deDiccionario),
  ].filter(noDegenerada);
}

/**
 * Descarta propuestas degeneradas: `original === propuesto` no propone nada
 * (ruido de la expansión mínima heredada de BUG-5 — tokens sin punto como
 * «C»/«DE» generan reglas «sin cambio en este elemento»). Detectado en la
 * revisión visual S1-3a 15-jul sobre Colomera real. Solo aplica a `texto`;
 * en `coordenada` la igualdad exacta ya la impide el umbral 0.5 del detector.
 */
function noDegenerada(p: Propuesta): boolean {
  return !(typeof p.original === 'string' && p.original === p.propuesto);
}
